import { useCallback, useEffect, useState } from 'react';

/** What the user picked. "system" follows the device's light/dark setting. */
export type ThemeChoice = 'dark' | 'light' | 'system';

const STORAGE_KEY = 'obdai.theme';
const systemPrefersLight = () => window.matchMedia?.('(prefers-color-scheme: light)').matches ?? false;

export function getThemeChoice(): ThemeChoice {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'dark' || v === 'light' || v === 'system') return v;
  } catch {
    /* private mode: fall through to the default */
  }
  return 'dark'; // the dashboard's own look; nobody is switched to light without asking for it
}

/** The theme actually on screen for a given choice. */
export function resolveTheme(choice: ThemeChoice): 'dark' | 'light' {
  return choice === 'system' ? (systemPrefersLight() ? 'light' : 'dark') : choice;
}

/** index.html applies the same attribute before first paint, so there is no dark-to-light flash. */
export function applyTheme(choice: ThemeChoice) {
  document.documentElement.dataset.theme = resolveTheme(choice);
}

export function useTheme() {
  const [choice, setChoiceState] = useState<ThemeChoice>(getThemeChoice);

  const setChoice = useCallback((next: ThemeChoice) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* still applies for this visit */
    }
    setChoiceState(next);
    applyTheme(next);
  }, []);

  // While following the device, change with it (e.g. the OS switching to dark at sunset).
  useEffect(() => {
    applyTheme(choice);
    if (choice !== 'system' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = () => applyTheme('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [choice]);

  return { choice, setChoice, resolved: resolveTheme(choice) };
}
